import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Building2,
  CalendarDays,
  Check,
  Clock,
  CreditCard,
  Pencil,
  Loader2,
  User,
  X,
  Zap,
} from "lucide-react";
import { Link } from "react-router";
import { getIndividualFullItemReport, getSubsFullItemReport } from "../payment-dues.api";
import { FullPaymentDueReport, IndividualPaymentDue, PaymentDueReportRow, PaymentDuesFilters, SubsPaymentDue } from "../payment-dues.entity";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import moment from "moment";
import type { PayerProfile } from "@/services/stripe-payer-profile.service";
import { autopayStateKey, type RecipientAutopayState } from "@/services/vendor-autopay.service";
import AutopaySection from "./AutopaySection";

type RecipientType = "individual" | "subs";

interface PaymentDueSheetProps {
  isOpen: boolean;
  onClose: () => void;
  due: IndividualPaymentDue | SubsPaymentDue | null;
  recipientType: RecipientType;
  activeFilters?: Pick<PaymentDuesFilters, "fromDate" | "toDate">;
  onUpdatePaymentTerms: (
    due: IndividualPaymentDue | SubsPaymentDue,
    type: RecipientType,
    paymentTermsDays: number
  ) => Promise<void> | void;
  paymentTermsSavingKey: string | null;
  autopayStates: Map<string, RecipientAutopayState>;
  autopayLoading: boolean;
  payerProfile: PayerProfile | null;
  onAutopayChange: (state: RecipientAutopayState) => void;
}

export default function PaymentDueSheet({
  isOpen,
  onClose,
  due,
  recipientType,
  activeFilters,
  onUpdatePaymentTerms,
  paymentTermsSavingKey,
  autopayStates,
  autopayLoading,
  payerProfile,
  onAutopayChange,
}: PaymentDueSheetProps) {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<FullPaymentDueReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [editingPaymentTerms, setEditingPaymentTerms] = useState(false);
  const [paymentTermsInput, setPaymentTermsInput] = useState("");
  const [paymentTermsError, setPaymentTermsError] = useState<string | null>(null);
  const dueId = due?.id;
  const duePaymentTermsDays = due?.payment_terms_days;

  useEffect(() => {
    if (!isOpen || !dueId) {
      setReport(null);
      setError(null);
      setStatusFilter("");
      setPage(1);
      setEditingPaymentTerms(false);
      setPaymentTermsInput("");
      setPaymentTermsError(null);
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, 350);
      return () => clearTimeout(timer);
    }

    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const filters = {
          fromDate: activeFilters?.fromDate ?? undefined,
          toDate: activeFilters?.toDate ?? undefined,
        };
        const data =
          recipientType === "individual"
            ? await getIndividualFullItemReport(dueId, filters)
            : await getSubsFullItemReport(dueId, filters);
        setReport(data);
      } catch (err: any) {
        setError(err?.message ?? "Failed to load items.");
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [isOpen, dueId, recipientType, activeFilters?.fromDate, activeFilters?.toDate]);

  useEffect(() => {
    if (!isOpen || !dueId || duePaymentTermsDays == null) return;
    setEditingPaymentTerms(false);
    setPaymentTermsInput(String(duePaymentTermsDays));
    setPaymentTermsError(null);
  }, [isOpen, dueId, duePaymentTermsDays]);

  const typeLabel = recipientType === "individual" ? "Individual" : "Subcontractor";
  const TypeIcon = recipientType === "individual" ? User : Building2;

  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

  const statusBadge = (status?: string) => {
    if (status === "paid") return <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-xs">Paid</Badge>;
    if (status === "partial") return <Badge variant="secondary" className="text-xs">Partial</Badge>;
    return <Badge variant="destructive" className="text-xs">Unpaid</Badge>;
  };

  const filteredItems = useMemo<PaymentDueReportRow[]>(() => {
    if (!report) return [];
    if (statusFilter === "") return report.items;
    if (statusFilter === "paid") return report.items.filter((i) => i.paymentStatus === "paid");
    return report.items.filter((i) => i.paymentStatus !== "paid");
  }, [report, statusFilter]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  const statusFilterItem: FilterItem = {
    type: "select",
    title: "Status",
    value: statusFilter,
    onValueChange: (val: string) => setStatusFilter(val),
    options: [
      { label: "Paid", value: "paid" },
      { label: "Unpaid", value: "unpaid" },
    ],
  };

  const columns: ColumnDef<PaymentDueReportRow>[] = [
    {
      header: "Type",
      accessor: (row) => (row.type === "appointment" ? "Appt" : "DW"),
      cellClassName: "text-sm",
    },
    {
      header: "Description",
      accessor: (row) => (
        <div>
          <span className="truncate block">{row.propertyName || "—"}</span>
          {row.unitNumber && (
            <span className="text-xs text-muted-foreground">Unit: {row.unitNumber}</span>
          )}
        </div>
      ),
      cellClassName: "text-sm max-w-[200px]",
    },
    {
      header: "Date",
      accessor: (row) =>
        row.date ? (
          <span className="flex items-center gap-1 text-muted-foreground">
            <CalendarDays className="h-3 w-3" />
            {moment(row.date).format("MM/DD/YYYY")}
          </span>
        ) : (
          "—"
        ),
      cellClassName: "text-sm",
    },
    {
      header: "Hours",
      accessor: (row) =>
        row.type === "daily_work_task" && row.hoursWorked != null ? (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Clock className="h-3 w-3" />
            {row.hoursWorked.toFixed(1)}h
          </span>
        ) : (
          "—"
        ),
      cellClassName: "text-sm",
    },
    {
      header: "Status",
      accessor: (row) => statusBadge(row.paymentStatus),
    },
    {
      header: "Amount",
      accessor: (row) => formatCurrency(row.amount),
      className: "text-right",
      cellClassName: "text-right text-sm",
    },
    {
      header: "",
      accessor: (row) => (
        <Link to={row.detailPath} className="text-primary text-sm hover:underline">
          View
        </Link>
      ),
    },
  ];

  if (!due) return null;

  const remainingApptAmount = due.remaining_appointment_amount ?? 0;
  const totalApptAmount = due.total_appointment_amount ?? remainingApptAmount;
  const remainingDWAmount = due.remaining_daily_work_amount ?? 0;
  const totalDWAmount = due.total_daily_work_amount ?? remainingDWAmount;
  const totalAppt = due.total_appointment_count ?? due.appointment_count;
  const totalDW = due.total_daily_work_count ?? due.daily_work_count;

  const hasDateRange = activeFilters?.fromDate || activeFilters?.toDate;
  const paymentTermsKey = `${recipientType}:${due.id}`;
  const savingPaymentTerms = paymentTermsSavingKey === paymentTermsKey;
  const displayPaymentTermsDays = Number.isInteger(Number(paymentTermsInput))
    ? Number(paymentTermsInput)
    : due.payment_terms_days;

  const handlePaymentTermsSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const parsedPaymentTermsDays = Number(paymentTermsInput);
    if (!Number.isInteger(parsedPaymentTermsDays) || parsedPaymentTermsDays < 1 || parsedPaymentTermsDays > 90) {
      setPaymentTermsError("Enter a whole number from 1 to 90.");
      return;
    }

    if (parsedPaymentTermsDays === due.payment_terms_days) {
      setEditingPaymentTerms(false);
      setPaymentTermsError(null);
      return;
    }

    try {
      await onUpdatePaymentTerms(due, recipientType, parsedPaymentTermsDays);
      setPaymentTermsInput(String(parsedPaymentTermsDays));
      setEditingPaymentTerms(false);
      setPaymentTermsError(null);
    } catch (err: any) {
      setPaymentTermsError(err?.message ?? "Failed to update payment terms.");
    }
  };

  const handlePaymentTermsCancel = () => {
    setPaymentTermsInput(String(due.payment_terms_days));
    setPaymentTermsError(null);
    setEditingPaymentTerms(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="z-[60] sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <TypeIcon className="h-4 w-4 text-primary" />
            </div>
            {due.name}
          </SheetTitle>
          <SheetDescription>Payment details for this {typeLabel.toLowerCase()}</SheetDescription>
        </SheetHeader>

        <div className="px-4 pb-4 space-y-5">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-xs">
              {typeLabel}
            </Badge>
            {due.ready_for_payouts ? (
              <Badge variant="default" className="gap-1 text-xs">
                <Zap className="h-3 w-3" />
                Ready for payouts
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1 text-xs">
                <CreditCard className="h-3 w-3" />
                Not ready for payouts
              </Badge>
            )}
          </div>

          <div className="rounded-lg border p-3 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Payment Terms</p>
                {!editingPaymentTerms ? (
                  <div className="mt-1 flex items-center gap-1.5">
                    <span
                      className="text-sm font-medium"
                      data-testid={`payment-terms-detail-${recipientType}-${due.id}`}
                    >
                      Net {displayPaymentTermsDays}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={`Edit payment terms for ${due.name}`}
                      data-testid={`payment-terms-edit-${recipientType}-${due.id}`}
                      onClick={() => {
                        setPaymentTermsInput(String(due.payment_terms_days));
                        setPaymentTermsError(null);
                        setEditingPaymentTerms(true);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {savingPaymentTerms && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                  </div>
                ) : (
                  <form className="mt-2 flex items-start gap-2" noValidate onSubmit={handlePaymentTermsSubmit}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Net</span>
                      <Input
                        type="number"
                        min={1}
                        max={90}
                        step={1}
                        inputMode="numeric"
                        value={paymentTermsInput}
                        disabled={savingPaymentTerms}
                        aria-invalid={Boolean(paymentTermsError)}
                        aria-label={`Payment terms days for ${due.name}`}
                        data-testid={`payment-terms-input-${recipientType}-${due.id}`}
                        className="w-20"
                        onChange={(event) => {
                          setPaymentTermsInput(event.target.value);
                          setPaymentTermsError(null);
                        }}
                      />
                    </div>
                    <Button
                      type="submit"
                      size="icon"
                      className="h-9 w-9"
                      disabled={savingPaymentTerms}
                      aria-label="Save payment terms"
                      data-testid={`payment-terms-save-${recipientType}-${due.id}`}
                    >
                      {savingPaymentTerms ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      disabled={savingPaymentTerms}
                      aria-label="Cancel payment terms edit"
                      onClick={handlePaymentTermsCancel}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </form>
                )}
              </div>
            </div>
            {paymentTermsError && <p className="text-xs text-destructive">{paymentTermsError}</p>}
          </div>

          {/* Autopay */}
          <AutopaySection
            recipientType={recipientType}
            due={due}
            autopayState={autopayStates.get(autopayStateKey(recipientType, due.id)) ?? null}
            autopayLoading={autopayLoading}
            payerProfile={payerProfile}
            onAutopayChange={onAutopayChange}
          />

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-3">
            {/* Amount Due */}
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 p-3">
              <p className="text-xs text-muted-foreground">Amount Due</p>
              <p className="text-lg font-bold text-destructive">{formatCurrency(due.remaining_amount)}</p>
              <p className="text-xs text-muted-foreground">
                of {formatCurrency(due.total_amount ?? due.remaining_amount)} total
              </p>
            </div>

            {/* Appointments */}
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Appointments</p>
              <p className="text-lg font-semibold">{due.appointment_count} of {totalAppt} unpaid</p>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(remainingApptAmount)} of {formatCurrency(totalApptAmount)}
              </p>
            </div>

            {/* Daily Work */}
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Daily Work</p>
              <p className="text-lg font-semibold">{due.daily_work_count} of {totalDW} unpaid</p>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(remainingDWAmount)} of {formatCurrency(totalDWAmount)}
              </p>
            </div>
          </div>

          {/* Items List */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">All Items</h4>
              {hasDateRange && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" />
                  {activeFilters?.fromDate ? moment(activeFilters.fromDate).format("MMM DD, YYYY") : "Start"}
                  {" – "}
                  {activeFilters?.toDate ? moment(activeFilters.toDate).format("MMM DD, YYYY") : "Now"}
                </span>
              )}
            </div>

            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {error && (
              <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {!loading && !error && report && (
              <SimpleTable<PaymentDueReportRow>
                columns={columns}
                data={paginatedItems}
                totalCount={filteredItems.length}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                filters={[statusFilterItem]}
                onReset={() => setStatusFilter("")}
                container={true}
                getRowClassName={(row) =>
                  row.paymentStatus === "paid" ? "bg-green-50/50 dark:bg-green-950/10" : ""
                }
              />
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
